import {RRule} from "rrule";
import Immutable from "immutable";

const frequencyOptions = Immutable.List([
  Immutable.Map({value: RRule.DAILY, label: "Day", labelPlural: "Days"}),
  Immutable.Map({value: RRule.WEEKLY, label: "Week", labelPlural: "Weeks"}),
  Immutable.Map({value: RRule.MONTHLY, label: "Month", labelPlural: "Months"}),
  Immutable.Map({value: RRule.YEARLY, label: "Year", labelPlural: "Years"})
]);

const weekdayOptions = Immutable.List([
  Immutable.Map({value: RRule.MO.weekday, label: "Monday"}),
  Immutable.Map({value: RRule.TU.weekday, label: "Tuesday"}),
  Immutable.Map({value: RRule.WE.weekday, label: "Wednesday"}),
  Immutable.Map({value: RRule.TH.weekday, label: "Thursday"}),
  Immutable.Map({value: RRule.FR.weekday, label: "Friday"}),
  Immutable.Map({value: RRule.SA.weekday, label: "Saturday"}),
  Immutable.Map({value: RRule.SU.weekday, label: "Sunday"})
]);

const monthOptions = Immutable.List([
  Immutable.Map({value: 1, label: "January"}),
  Immutable.Map({value: 2, label: "February"}),
  Immutable.Map({value: 3, label: "March"}),
  Immutable.Map({value: 4, label: "April"}),
  Immutable.Map({value: 5, label: "May"}),
  Immutable.Map({value: 6, label: "June"}),
  Immutable.Map({value: 7, label: "July"}),
  Immutable.Map({value: 8, label: "August"}),
  Immutable.Map({value: 9, label: "September"}),
  Immutable.Map({value: 10, label: "October"}),
  Immutable.Map({value: 11, label: "November"}),
  Immutable.Map({value: 12, label: "December"})
]);

const getDefaultRuleConfig = () =>
  Immutable.Map({
    freq: RRule.MONTHLY,
    interval: 1,
    count: 1,
    until: new Date().toISOString().split("T")[0],
    byweekday: Immutable.List(),
    bymonthday: Immutable.List(),
    bymonth: Immutable.List(),
    bysetpos: null,
    endMode: "never",
    monthlyPattern: "dayOfMonth",
    startDate: new Date().toISOString().split("T")[0],
    startTime: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });

const getDefaultSchedule = () => {
  const config = getDefaultRuleConfig();
  return getScheduleFromConfig(config, getMonthlyPatternInfo(config.get("startDate")));
}

const getConfigFromSchedule = (schedule) => {
  const rule = RRule.fromString(schedule.get("rrule"));
  return Immutable.Map({
    freq: rule.options.freq,
    interval: rule.options.interval || 1,
    count: rule.options.count || 1,
    until: rule.options.until ? rule.options.until.toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
    byweekday: rule.options.byweekday ? Immutable.List(rule.options.byweekday) : Immutable.List(),
    bymonthday: rule.options.bymonthday ? Immutable.List(rule.options.bymonthday) : Immutable.List(),
    bymonth: rule.options.bymonth ? Immutable.List(rule.options.bymonth) : Immutable.List(),
    bysetpos: rule.options.bysetpos || null,
    endMode: rule.options.count ? "count" : rule.options.until ? "until" : "never",
    monthlyPattern: rule.options.bysetpos ? "dayOfWeek" : "dayOfMonth",
    startDate: schedule.get("startDate"),
    startTime: schedule.get("startTime"),
    timezone: schedule.get("timezone")
  });
};

const getMonthlyPatternInfo = (startDate) => {
  const date = new Date(startDate);
  const dayOfMonth = date.getUTCDate();
  const dayOfWeek = date.getUTCDay();

  const rruleWeekday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const weekOfMonth = Math.ceil(dayOfMonth / 7);

  const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const ordinals = ["First", "Second", "Third", "Fourth", "Fifth"];

  return {
    dayOfMonth,
    dayOfWeek,
    rruleWeekday,
    weekOfMonth,
    dayName: weekdayNames[dayOfWeek],
    ordinal: ordinals[weekOfMonth - 1]
  };
};

const getScheduleFromConfig = (config, monthlyPatternInfo) => {
  const options = {
    freq: config.get("freq"),
    interval: config.get("interval") || 1
  };

  if (options.freq === undefined || options.freq === null) {
    return null;
  }

  if (config.get("endMode") === "count" && config.get("count")) {
    options.count = config.get("count");
  } else if (config.get("endMode") === "until" && config.get("until")) {
    const untilDate = new Date(config.get("until"));

    if (!isNaN(untilDate.getTime())) {
      options.until = untilDate;
    }
  }

  switch (config.get("freq")) {
    case RRule.DAILY: break
    case RRule.WEEKLY: {
      if (config.get("byweekday") && !config.get("byweekday").isEmpty()) {
        options.byweekday = config.get("byweekday").toArray();
      }
      break;
    }
    case RRule.MONTHLY: {
      if (config.get("monthlyPattern") === "dayOfMonth") {
        options.bymonthday = [monthlyPatternInfo.dayOfMonth];
      } else if (config.get("monthlyPattern") === "dayOfWeek") {
        const info = monthlyPatternInfo;
        options.byweekday = [info.rruleWeekday];
        options.bysetpos = info.weekOfMonth;
      }
      break;
    }
    case RRule.YEARLY: {
      if (config.get("bymonth") && !config.get("bymonth").isEmpty()) {
        options.bymonth = config.get("bymonth").toArray();
      }
      break;
    }
    default: throw new Error("Unsupported schedule frequency");
  }

  try {
    const rruleObj = new RRule(options);
    const rrule = rruleObj.toString().split(":")[1];
    return Immutable.Map({
      rrule,
      startDate: config.get("startDate"),
      startTime: config.get("startTime"),
      timezone: config.get("timezone"),
    })
  } catch (e) {
    console.error("Error generating RRule:", e);
    return null;
  }
};

export {
  frequencyOptions,
  weekdayOptions,
  monthOptions,
  getDefaultSchedule,
  getDefaultRuleConfig,
  getConfigFromSchedule,
  getMonthlyPatternInfo,
  getScheduleFromConfig
};
